// Error tracking (ADR 0025), off unless the deployment sets SENTRY_DSN. The web
// container's entrypoint writes it into index.html as <meta name="sentry-dsn">, with the
// release and environment beside it. The SDK is a separate chunk, loaded only then, so a
// deployment without Sentry downloads none of it.
import { ApiError } from '../api/client';
import type * as SentryModule from './sentry';

type Sdk = typeof SentryModule;

let sdk: Sdk | null = null;
let enabled = false;
const pending: unknown[] = [];

function meta(name: string): string | undefined {
  return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content || undefined;
}

/** Starts error tracking if this deployment has a DSN. */
export function startErrorTracking(): void {
  const dsn = meta('sentry-dsn');
  if (!dsn) return;
  enabled = true;
  void import('./sentry').then((loaded) => {
    loaded.init({
      dsn,
      release: meta('sentry-release'),
      environment: meta('sentry-environment') ?? 'production',
    });
    sdk = loaded;
    for (const error of pending.splice(0)) report(loaded, error);
  });
}

/**
 * Sends an error the app caught itself (an error page). Uncaught errors and rejections
 * are the SDK's own. An ApiError carries the request ID that is also in the API's logs.
 */
export function reportError(error: unknown): void {
  if (sdk) report(sdk, error);
  else if (enabled) pending.push(error);
}

function report(loaded: Sdk, error: unknown) {
  loaded.capture(
    error,
    error instanceof ApiError ? (error.problem.requestId ?? undefined) : undefined,
  );
}
